import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Chip,
  Divider,
  TextField,
  CardActions,
  Tooltip,
  IconButton,
} from "@mui/material";
import MDBox from "components/MDBox";
import { TrendingUp, WbSunny, NightsStay, ArrowForward, Launch } from "@mui/icons-material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { updateGaharRatios } from "services/api";
import MDButton from "components/MDButton";

const GAHAR_OPTIMUMS = {
  "icu nurse": { day: 1, night: 2 },
  "icu specialist": { day: 10, night: 14 },
  "or nurse": { day: 0.5, night: 0.5 },
  surgeon: { day: 1, night: 1 },
  "ward nurse": { day: 4, night: 6 },
  "internal resident": { day: 15, night: 30 },
  "clinic nurse": { day: 4, night: null },
  "clinic consultant": { day: 20, night: null },
};

// --- Job Card Component ---
const GaharJobCard = ({ job, onEdit, setJobs }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = React.useState(null);
  // "day" | "night" | null

  const [tempValue, setTempValue] = React.useState("");

  const editRef = React.useRef(null);
  const noSpinnerStyle = {
    "& input[type=number]": { "-moz-appearance": "textfield" },
    "& input[type=number]::-webkit-outer-spin-button": { "-webkit-appearance": "none", margin: 0 },
    "& input[type=number]::-webkit-inner-spin-button": { "-webkit-appearance": "none", margin: 0 },
    "& .MuiInputBase-input": {
      padding: "0px",
      textAlign: "center",
      fontWeight: "bold",
      fontSize: "0.75rem",
    },
  };

  useEffect(() => {
    async function handleClickOutside(event) {
      if (editing && editRef.current && !editRef.current.contains(event.target)) {
        const isDay = editing === "day";

        // 1. Parse the new value (Handle empty string)
        let newValue = tempValue === "" ? null : Number(tempValue);

        // 2. Get the correct original value based on your data structure
        // Previous context indicated these are named 'firstRatio' and 'secondRatio'
        const originalValue = isDay ? job.firstRatio : job.secondRatio;

        // 3. Comparison Logic
        // We check if both are null/undefined (equal) OR if the numbers match
        const isSame = (newValue == null && originalValue == null) || newValue === originalValue;

        if (!isSame) {
          try {
            const res = await updateGaharRatios(job.id, newValue, isDay);

            if (res && res !== "error") {
              setJobs((prevJobs) => prevJobs.map((j) => (j.id === res.id ? { ...j, ...res } : j)));
            }
          } catch (error) {
            console.error("Failed to update ratio", error);
          }
        }

        setEditing(null);
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on cleanup
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editing, tempValue, job]); // Dependencies ensure 'tempValue' is fresh inside the listener

  const diff = job.diff ?? null;
  const utilization = job?.utilization == 0 || job.utilization == null ? null : job.utilization;
  const fte = job.numberOfEmployees ?? 0;

  // --- 1. STAFFING STATUS LOGIC (Your custom logic) ---
  let staffingStatus = "balanced";
  let staffingColor = "success.main"; // Default Green

  if (typeof diff === "number" && !isNaN(diff)) {
    if (diff < 0) {
      staffingStatus = "understaffed";
      staffingColor = "error.main"; // Red
    } else if (diff > 0) {
      // Specific rule: if only 1 person, call it balanced even if diff > 0
      if (fte === 1) {
        staffingStatus = "balanced";
        staffingColor = "success.main";
      } else {
        staffingStatus = "overstaffed";
        staffingColor = "warning.main"; // Orange
      }
    } else {
      staffingStatus = "balanced";
      staffingColor = "success.main";
    }
  }

  // --- 2. WORKLOAD LOGIC (Utilization) ---
  let workloadStatus = "balanced";
  let workloadChipColor = "success"; // Chip prop uses "success", "warning", "error" strings

  if (typeof utilization === "number" && !isNaN(utilization) && utilization > 0) {
    if (utilization > 115) {
      workloadStatus = "overworked";
      workloadChipColor = "error";
    } else if (utilization < 90) {
      workloadStatus = "underworked";
      workloadChipColor = "warning";
    } else {
      workloadStatus = "balanced";
      workloadChipColor = "success";
    }
  }

  // --- 3. Tooltip Construction (Optional visual aid) ---
  const tooltipText = `${t(`user.gahar.status.${staffingStatus}`)} & ${t(`user.gahar.status.${workloadStatus}`)}`;

  // 1. Get Optimums based on Job Name
  const normalizedName = job.jobName?.trim().toLowerCase();
  const optimums = GAHAR_OPTIMUMS[normalizedName];

  // 2. Helper to check deviation
  // Returns { isDiff: boolean, message: string }
  const getDeviationInfo = (currentVal, optVal) => {
    // If no optimum defined, or values match, return false
    if (optVal === undefined || optVal === null || currentVal == Number(optVal)) {
      return { isDiff: false, message: "" };
    }
    return {
      isDiff: true,
      message: `${t("user.gahar.Optimum")}: 1:${optVal}`, // e.g., "Optimum: 1:5"
    };
  };

  const dayDev = getDeviationInfo(job.firstRatio, optimums?.day);
  const nightDev = getDeviationInfo(job.secondRatio, optimums?.night);

  return (
    <Card
      sx={{
        mb: 1.5, // Reduced bottom margin between cards
        boxShadow: 2,
        border: "1px solid #e0e0e0",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
      }}
    >
      {/* Reduced padding from 2 (16px) to 1.5 (12px) */}
      <MDBox p={1.5}>
        {/* 1. HEADER: Title + Chip + Action Icon */}
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={0.5}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ maxWidth: "60%", fontSize: "0.85rem", lineHeight: 1.2 }}
          >
            {job.jobName}
          </Typography>

          <Box display="flex" alignItems="center" gap={0.5}>
            {utilization != null && (
              <Chip
                icon={<TrendingUp style={{ fontSize: 12 }} />}
                label={`${utilization}%`}
                color={workloadChipColor}
                size="small"
                sx={{
                  height: 18, // Smaller chip
                  fontSize: "9px",
                  fontWeight: "bold",
                  opacity: 0.9,
                  "& .MuiChip-label": { px: 0.5 },
                }}
              />
            )}

            {/* ✅ NEW COMPACT EDIT/DETAILS BUTTON */}
            <Tooltip title={t("Open Details")} arrow>
              <IconButton
                size="small"
                onClick={onEdit}
                sx={{
                  padding: "2px",
                  ml: 0.5,
                  bgcolor: "rgba(0,0,0,0.04)",
                  "&:hover": { bgcolor: "info.main", color: "white" },
                }}
              >
                <Launch sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* 2. Ratios (Day & Night) */}
        <Box display="flex" flexDirection="column" gap={0.5} mb={1.5}>
          {/* --- DAY RATIO --- */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={0.5}>
              <WbSunny sx={{ fontSize: 14, color: "#f39c12" }} />
              <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                {t("user.gahar.dayRatio")}:
              </Typography>
            </Box>

            <Box
              onDoubleClick={() => {
                setEditing("day");
                setTempValue(job.firstRatio);
              }}
              sx={{ cursor: "pointer", minWidth: 40, textAlign: "right" }}
            >
              {editing === "day" ? (
                <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  <TextField
                    inputRef={editRef}
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    size="small"
                    type="number"
                    autoFocus
                    sx={{ width: 50, ...noSpinnerStyle }}
                  />
                </Box>
              ) : (
                // 👇 UPDATED: Added Tooltip and Color Logic
                <Tooltip title={dayDev.message} arrow placement="top">
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    fontSize="0.75rem"
                    // If different from optimum, turn Orange (Warning)
                    sx={{ color: dayDev.isDiff ? "warning.main" : "text.primary" }}
                  >
                    1:{job.firstRatio ?? "-"}
                    {/* Optional: Add a subtle asterisk if diff */}
                    {dayDev.isDiff && "*"}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* --- NIGHT RATIO --- */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              visibility: job.secondRatio != null ? "visible" : "hidden",
              height: "auto",
            }}
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <NightsStay sx={{ fontSize: 14, color: "#34495e" }} />
              <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                {t("user.gahar.nightRatio")}:
              </Typography>
            </Box>

            <Box
              onDoubleClick={() => {
                setEditing("night");
                setTempValue(job.secondRatio);
              }}
              sx={{ cursor: "pointer", minWidth: 40, textAlign: "right" }}
            >
              {editing === "night" ? (
                <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  <TextField
                    inputRef={editRef}
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    size="small"
                    type="number"
                    autoFocus
                    sx={{ width: 50, ...noSpinnerStyle }}
                  />
                </Box>
              ) : (
                // 👇 UPDATED: Added Tooltip and Color Logic
                <Tooltip title={nightDev.message} arrow placement="top">
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    fontSize="0.75rem"
                    sx={{ color: nightDev.isDiff ? "warning.main" : "text.primary" }}
                  >
                    1:{job.secondRatio ?? "-"}
                    {nightDev.isDiff && "*"}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        {/* 3. Footer Statistics - Compact */}
        <Box
          sx={{
            bgcolor: "#f8f9fa",
            p: 0.75, // Reduced padding
            borderRadius: 1,
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            textAlign: "center",
            gap: 0.5,
          }}
        >
          {/* Actual */}
          <Box>
            <Typography variant="caption" display="block" color="text.secondary" fontSize="0.65rem">
              {t("user.gahar.actual")}
            </Typography>
            <Typography variant="caption" fontWeight="bold" fontSize="0.8rem">
              {fte}
            </Typography>
          </Box>

          {/* Required */}
          <Box sx={{ borderLeft: "1px solid #ddd", borderRight: "1px solid #ddd" }}>
            <Typography variant="caption" display="block" color="text.secondary" fontSize="0.65rem">
              {t("user.gahar.required")}
            </Typography>
            <Typography variant="caption" fontWeight="bold" color="info.main" fontSize="0.8rem">
              {job.requiredFTE ?? "-"}
            </Typography>
          </Box>

          {/* Gap */}
          <Box>
            <Typography variant="caption" display="block" color="text.secondary" fontSize="0.65rem">
              {t("user.gahar.gap")}
            </Typography>
            <Box title={tooltipText} sx={{ cursor: "help" }}>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ color: staffingColor, fontSize: "0.8rem" }}
              >
                {diff != null ? (diff > 0 ? `+${diff}` : diff) : "-"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </MDBox>
    </Card>
  );
};

GaharJobCard.propTypes = {
  job: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  setJobs: PropTypes.func.isRequired,
};

export default GaharJobCard;
